import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Snowflake, Plus, Trash2, Edit2, Settings, Package, X, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { formatDate } from '@/utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const FrozenChickenCount = () => {
  const currencySymbol = localStorage.getItem('currency_symbol') || '₦';
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin';
  const isMgmt = isAdmin || currentUser?.role === 'manager';

  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([]);
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);

  // Add / edit stock dialog
  const [stockOpen, setStockOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [stockForm, setStockForm] = useState({ date: new Date().toISOString().split('T')[0], variant_key: '', packs_added: '', note: '' });

  // Variant management dialog
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [variantForm, setVariantForm] = useState({ label: '', weight_kg: '', default_price: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/frozen-chicken/stock`, getAuthHeader());
      setVariants(res.data.variants || []);
      setSummary(res.data.summary || []);
      setEntries(res.data.entries || []);
    } catch (e) {
      toast.error('Failed to load frozen chicken stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAddStock = () => {
    setEditingEntry(null);
    setStockForm({ date: new Date().toISOString().split('T')[0], variant_key: variants[0]?.key || '', packs_added: '', note: '' });
    setStockOpen(true);
  };

  const openEditStock = (e) => {
    setEditingEntry(e);
    setStockForm({ date: e.date, variant_key: e.variant_key, packs_added: e.packs_added?.toString() || '', note: e.note || '' });
    setStockOpen(true);
  };

  const submitStock = async (ev) => {
    ev.preventDefault();
    if (!stockForm.variant_key) { toast.error('Select a pack weight'); return; }
    const payload = {
      date: stockForm.date,
      variant_key: stockForm.variant_key,
      packs_added: parseInt(stockForm.packs_added) || 0,
      note: stockForm.note || null,
    };
    try {
      if (editingEntry) {
        await axios.put(`${API}/frozen-chicken/stock/${editingEntry.id}`, payload, getAuthHeader());
        toast.success('Stock entry updated');
      } else {
        await axios.post(`${API}/frozen-chicken/stock`, payload, getAuthHeader());
        toast.success('Stock added');
      }
      setStockOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save stock');
    }
  };

  const deleteStock = async (id) => {
    if (!window.confirm('Delete this stock entry?')) return;
    try {
      await axios.delete(`${API}/frozen-chicken/stock/${id}`, getAuthHeader());
      toast.success('Stock entry deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const addVariant = async (ev) => {
    ev.preventDefault();
    const w = parseFloat(variantForm.weight_kg);
    if (!w || w <= 0) { toast.error('Enter a valid weight in kg'); return; }
    try {
      await axios.post(`${API}/frozen-chicken/variants`, {
        label: variantForm.label || `${w}kg Pack`,
        weight_kg: w,
        default_price: parseFloat(variantForm.default_price) || 0,
      }, getAuthHeader());
      toast.success('Pack variant added');
      setVariantForm({ label: '', weight_kg: '', default_price: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add variant');
    }
  };

  const deleteVariant = async (key) => {
    if (!window.confirm('Remove this pack variant? Existing records keep their data.')) return;
    try {
      await axios.delete(`${API}/frozen-chicken/variants/${key}`, getAuthHeader());
      toast.success('Variant removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove variant');
    }
  };

  const variantLabel = (key) => variants.find(v => v.key === key)?.label || key;

  return (
    <div className="space-y-6" data-testid="frozen-chicken-count-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center shrink-0">
            <Snowflake className="w-6 h-6 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Frozen Chicken Count</h1>
            <p className="text-sm text-slate-500">Track frozen chicken stock by pack weight. Sales from Customer Pick-up reduce stock automatically.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setVariantsOpen(true)} data-testid="manage-variants-btn">
              <Settings className="w-4 h-4 mr-2" /> Pack Weights
            </Button>
          )}
          <Button onClick={openAddStock} className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-stock-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Stock
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {summary.map(s => (
              <div key={s.key} className="rounded-xl border bg-white p-4 shadow-sm" data-testid={`frozen-summary-${s.key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{s.label || `${s.key} Pack`}</span>
                  <Package className="w-5 h-5 text-cyan-500" />
                </div>
                <div className="mt-3">
                  <p className={`text-3xl font-bold ${s.current < 0 ? 'text-rose-600' : 'text-slate-900'}`} data-testid={`frozen-current-${s.key}`}>{s.current}</p>
                  <p className="text-xs text-slate-500">packs in stock</p>
                  {s.current < 0 && (
                    <p className="text-xs text-rose-600 flex items-center gap-1 mt-1"><AlertTriangle className="w-3 h-3" /> Sold more than added</p>
                  )}
                </div>
                <div className="mt-3 flex justify-between text-xs text-slate-500 border-t pt-2">
                  <span>Added: <b className="text-slate-700">{s.added}</b></span>
                  <span>Sold: <b className="text-slate-700">{s.sold}</b></span>
                </div>
                <p className="text-xs text-slate-400 mt-1">Price: {currencySymbol}{(s.default_price || 0).toLocaleString()}/pack</p>
              </div>
            ))}
            {summary.length === 0 && (
              <p className="text-sm text-slate-400 col-span-full">No pack variants configured.</p>
            )}
          </div>

          {/* Stock additions table */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Stock Additions</h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="frozen-stock-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-sm text-slate-500">
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Pack Weight</th>
                    <th className="py-3 px-4 text-center">Packs Added</th>
                    <th className="py-3 px-4">Note</th>
                    <th className="py-3 px-4">Recorded By</th>
                    <th className="py-3 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">No stock additions recorded yet.</td></tr>
                  ) : entries.map(e => (
                    <tr key={e.id} className="border-b hover:bg-slate-50 text-sm" data-testid={`frozen-entry-${e.id}`}>
                      <td className="py-3 px-4">{formatDate(e.date)}</td>
                      <td className="py-3 px-4 font-medium">{variantLabel(e.variant_key)}</td>
                      <td className="py-3 px-4 text-center font-semibold text-emerald-700">+{e.packs_added}</td>
                      <td className="py-3 px-4 text-slate-500">{e.note || '—'}</td>
                      <td className="py-3 px-4 text-slate-500">{e.created_by_name || '—'}</td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center gap-1">
                          {isMgmt && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEditStock(e)} data-testid={`edit-stock-${e.id}`}><Edit2 className="w-4 h-4 text-blue-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteStock(e.id)} data-testid={`delete-stock-${e.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add / Edit Stock Dialog */}
      <Dialog open={stockOpen} onOpenChange={setStockOpen}>
        <DialogContent data-testid="stock-dialog">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Stock Addition' : 'Add Frozen Chicken Stock'}</DialogTitle>
            <DialogDescription>Record new packs added to the frozen chicken inventory.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitStock} className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input type="date" value={stockForm.date} onChange={(e) => setStockForm(p => ({ ...p, date: e.target.value }))} className="mt-1" data-testid="stock-date-input" />
            </div>
            <div>
              <Label>Pack Weight</Label>
              <Select value={stockForm.variant_key} onValueChange={(v) => setStockForm(p => ({ ...p, variant_key: v }))}>
                <SelectTrigger className="mt-1" data-testid="stock-variant-select"><SelectValue placeholder="Select pack weight" /></SelectTrigger>
                <SelectContent>
                  {variants.map(v => <SelectItem key={v.key} value={v.key}>{v.label || `${v.key} Pack`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Packs Added</Label>
              <Input type="number" min="0" value={stockForm.packs_added} onChange={(e) => setStockForm(p => ({ ...p, packs_added: e.target.value }))} className="mt-1" data-testid="stock-packs-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={stockForm.note} onChange={(e) => setStockForm(p => ({ ...p, note: e.target.value }))} placeholder="e.g. new delivery" className="mt-1" data-testid="stock-note-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStockOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" data-testid="stock-submit-btn">{editingEntry ? 'Update' : 'Add Stock'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Variants Dialog (admin) */}
      <Dialog open={variantsOpen} onOpenChange={setVariantsOpen}>
        <DialogContent data-testid="variants-dialog">
          <DialogHeader>
            <DialogTitle>Pack Weight Variants</DialogTitle>
            <DialogDescription>Add or remove frozen chicken pack weights. Prices are set in Price Management.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {variants.map(v => (
              <div key={v.key} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`variant-row-${v.key}`}>
                <div>
                  <p className="font-medium text-slate-800">{v.label || `${v.key} Pack`}</p>
                  <p className="text-xs text-slate-500">{v.weight_kg}kg · default {currencySymbol}{(v.default_price || 0).toLocaleString()}/pack</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteVariant(v.key)} data-testid={`delete-variant-${v.key}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
              </div>
            ))}
          </div>
          <form onSubmit={addVariant} className="space-y-3 border-t pt-3 mt-2">
            <p className="text-sm font-semibold text-slate-700">Add new variant</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Weight (kg)</Label>
                <Input type="number" min="0" step="0.1" value={variantForm.weight_kg} onChange={(e) => setVariantForm(p => ({ ...p, weight_kg: e.target.value }))} className="mt-1" data-testid="variant-weight-input" />
              </div>
              <div>
                <Label className="text-xs">Label</Label>
                <Input value={variantForm.label} onChange={(e) => setVariantForm(p => ({ ...p, label: e.target.value }))} placeholder="auto" className="mt-1" data-testid="variant-label-input" />
              </div>
              <div>
                <Label className="text-xs">Default Price</Label>
                <Input type="number" min="0" value={variantForm.default_price} onChange={(e) => setVariantForm(p => ({ ...p, default_price: e.target.value }))} className="mt-1" data-testid="variant-price-input" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="add-variant-btn"><Plus className="w-4 h-4 mr-2" /> Add Variant</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FrozenChickenCount;
